<template>
  <div
    v-if="isAccessChecked && isAdmin"
    class="pending-page"
  >

    <DefaultHeader
      title="Ивенты на модерации"
    />

    <main class="pending-content">

      <div class="pending-container">

        <!-- =====================================================
             HEADING
        ====================================================== -->

        <section class="pending-heading">

          <div>
            <span class="pending-label">
              Модерация
            </span>

            <h1>
              Ивенты на модерации
            </h1>

            <p>
              Проверьте информацию о событии перед публикацией.
              Новые и изменённые ивенты ожидают решения администратора.
            </p>
          </div>


          <div class="pending-counter">

            <span class="counter-dot"></span>

            <span>
              {{ counts.total }}
            </span>

            <span class="counter-caption">
              ожидают
            </span>

          </div>

        </section>


        <!-- =====================================================
             FILTERS
        ====================================================== -->

        <section
          v-if="!isLoading && !errorMessage && counts.total > 0"
          class="moderation-filters"
        >

          <button
            type="button"
            class="filter-button"
            :class="{ active: selectedFilter === 'all' }"
            @click="selectedFilter = 'all'"
          >
            Все

            <span class="filter-count">
              {{ counts.total }}
            </span>
          </button>


          <button
            type="button"
            class="filter-button"
            :class="{ active: selectedFilter === 'create' }"
            @click="selectedFilter = 'create'"
          >
            Новые

            <span class="filter-count">
              {{ counts.create }}
            </span>
          </button>


          <button
            type="button"
            class="filter-button"
            :class="{ active: selectedFilter === 'edit' }"
            @click="selectedFilter = 'edit'"
          >
            Изменения

            <span class="filter-count">
              {{ counts.edit }}
            </span>
          </button>

        </section>


        <!-- =====================================================
             LOADING
        ====================================================== -->

        <section
          v-if="isLoading"
          class="events-grid"
        >

          <div
            v-for="index in 4"
            :key="index"
            class="event-card skeleton-card"
          >

            <div class="skeleton-image"></div>

            <div class="skeleton-content">

              <div class="skeleton-row">
                <div class="skeleton-line small"></div>
                <div class="skeleton-line date"></div>
              </div>

              <div class="skeleton-line title"></div>

              <div class="skeleton-line"></div>

              <div class="skeleton-line short"></div>

              <div class="skeleton-meta">

                <div class="skeleton-pill"></div>

                <div class="skeleton-pill"></div>

              </div>

            </div>

          </div>

        </section>


        <!-- =====================================================
             ERROR
        ====================================================== -->

        <section
          v-else-if="errorMessage"
          class="state-card"
        >

          <div class="state-icon error">
            !
          </div>

          <h2>
            Не удалось загрузить ивенты
          </h2>

          <p>
            {{ errorMessage }}
          </p>

          <button
            type="button"
            class="state-button"
            @click="loadPendingEvents"
          >
            Попробовать снова
          </button>

        </section>


        <!-- =====================================================
             EMPTY
        ====================================================== -->

        <section
          v-else-if="counts.total === 0"
          class="state-card"
        >

          <div class="state-icon success">
            ✓
          </div>

          <h2>
            Всё проверено
          </h2>

          <p>
            Сейчас нет ивентов, ожидающих модерации.
          </p>

        </section>


        <!-- =====================================================
             FILTER EMPTY
        ====================================================== -->

        <section
          v-else-if="filteredEvents.length === 0"
          class="filter-empty"
        >

          <div class="filter-empty-icon">
            ✓
          </div>

          <div>
            <strong>
              Здесь пока пусто
            </strong>

            <span>
              В этой категории нет ивентов на модерации.
            </span>
          </div>

        </section>


        <!-- =====================================================
             EVENTS
        ====================================================== -->

        <section
          v-else
          class="events-grid"
        >

          <article
            v-for="event in filteredEvents"
            :key="event.id"
            class="event-card"
          >

            <!-- IMAGE -->

            <div class="event-image-wrap">

              <img
                v-if="event.image_url"
                :src="event.image_url"
                :alt="event.title || 'Ивент'"
                class="event-image"
              />

              <div
                v-else
                class="event-image-placeholder"
              >

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >

                  <rect
                    x="4"
                    y="5"
                    width="16"
                    height="15"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />

                  <path
                    d="M8 3V7"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />

                  <path
                    d="M16 3V7"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />

                  <path
                    d="M4 9H20"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />

                </svg>

              </div>


              <div
                class="moderation-badge"
                :class="moderationBadgeClass(event)"
              >
                {{ moderationTitle(event) }}
              </div>


              <div
                v-if="event.date_of_event"
                class="event-date-badge"
              >
                <span class="event-date-day">
                  {{ eventDay(event.date_of_event) }}
                </span>

                <span class="event-date-month">
                  {{ eventMonth(event.date_of_event) }}
                </span>
              </div>

            </div>


            <!-- BODY -->

            <div class="event-body">

              <div class="event-topline">

                <span class="event-category">
                  {{ eventCategory(event) }}
                </span>

                <span class="event-created-date">
                  {{ formatSubmittedDate(event) }}
                </span>

              </div>


              <h2 class="event-title">
                {{ event.title || "Без названия" }}
              </h2>


              <p
                v-if="event.preview_title"
                class="event-preview-title"
              >
                {{ event.preview_title }}
              </p>


              <!-- META -->

              <div class="event-meta">

                <div class="meta-item">

                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <rect
                      x="5"
                      y="5"
                      width="14"
                      height="14"
                      rx="3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M8 3V7"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />

                    <path
                      d="M16 3V7"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />

                    <path
                      d="M5 9H19"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                  </svg>

                  <span>
                    {{ formatEventDate(event.date_of_event) }}
                  </span>

                </div>


                <div
                  v-if="event.address_title"
                  class="meta-item"
                >

                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <circle
                      cx="12"
                      cy="10"
                      r="2"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                  </svg>

                  <span>
                    {{ event.address_title }}
                  </span>

                </div>


                <div
                  v-if="event.city_id"
                  class="meta-item"
                >

                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 20V8L12 4L19 8V20"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                    <path
                      d="M9 20V14H15V20"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                  </svg>

                  <span>
                    {{ event.city_id }}
                  </span>

                </div>

              </div>


              <!-- COMPANY -->

              <div class="company-card">

                <div class="company-logo">

                  <img
                    v-if="event.logo"
                    :src="event.logo"
                    :alt="event.name || 'Компания'"
                  />

                  <span v-else>
                    {{ companyInitial(event.name) }}
                  </span>

                </div>


                <div class="company-content">

                  <span class="company-label">
                    Компания
                  </span>

                  <span class="company-name">
                    {{ event.name || event.company_id || "Компания не указана" }}
                  </span>

                  <span
                    v-if="event.company_id"
                    class="company-id"
                  >
                    {{ event.company_id }}
                  </span>

                </div>

              </div>


              <!-- DESCRIPTION -->

              <p class="event-description">
                {{
                  event.description
                  || "Описание события отсутствует."
                }}
              </p>


              <!-- LINK -->

              <a
                v-if="event.link_url"
                :href="event.link_url"
                target="_blank"
                rel="noopener noreferrer"
                class="event-link"
                @click.stop
              >

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M10 14L14 10"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />

                  <path
                    d="M8.5 16.5L7 18C5.34 19.66 2.66 19.66 1 18C-0.66 16.34 -0.66 13.66 1 12L4 9C5.66 7.34 8.34 7.34 10 9"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />

                  <path
                    d="M15.5 7.5L17 6C18.66 4.34 21.34 4.34 23 6C24.66 7.66 24.66 10.34 23 12L20 15C18.34 16.66 15.66 16.66 14 15"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />
                </svg>

                <span>
                  Открыть ссылку события
                </span>

              </a>


              <!-- MODERATION INFO -->

              <div
                v-if="event.moderation_type === 'edit'"
                class="edit-info"
              >

                <div class="edit-info-icon">
                  ✎
                </div>

                <div>
                  <strong>
                    Изменение опубликованного ивента
                  </strong>

                  <span>
                    Текущая опубликованная версия продолжает работать
                    до подтверждения изменений.
                  </span>
                </div>

              </div>


              <!-- FOOTER -->

              <div class="event-footer">

                <div class="event-identifiers">

                  <div class="event-id">
                    EVENT ID:
                    <span>
                      {{ event.id }}
                    </span>
                  </div>

                  <div
                    v-if="
                      event.original_event_id &&
                      event.original_event_id !== event.id
                    "
                    class="event-id"
                  >
                    ORIGINAL:
                    <span>
                      {{ event.original_event_id }}
                    </span>
                  </div>

                </div>


                <button
                  type="button"
                  class="approve-button"
                  :disabled="approvingEventId === event.id"
                  @click="approveEvent(event)"
                >
                  <template
                    v-if="approvingEventId === event.id"
                  >
                    <span class="button-spinner"></span>

                    Публикуем...
                  </template>

                  <template v-else>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M5 12.5L9.5 17L19 7.5"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    Опубликовать
                  </template>
                </button>

              </div>

            </div>

          </article>

        </section>

      </div>

    </main>


    <Footer />

  <Transition name="snackbar">
    <div
      v-if="showSnackbar"
      class="snackbar"
    >
      <div class="snackbar-icon">
        ✓
      </div>

      <span>
        {{ snackbarMessage }}
      </span>
    </div>
  </Transition>

  </div>
</template>


<script setup>

import {
  waitForAuthReady
} from "../utils/auth.js";

import {
  computed,
  onMounted,
  ref
} from "vue";

import DefaultHeader
  from "../components/DefaultHeader.vue";

import Footer
  from "../components/MiniFooter.vue";

import AdminService
  from "../api/AdminService";

const isAccessChecked =
  ref(false);

  const isAdmin =
  ref(false);

const pendingEvents = ref([]);
const createEvents = ref([]);
const editEvents = ref([]);

const counts = ref({
  total: 0,
  create: 0,
  edit: 0
});

const approvingEventId =
  ref(null);

const selectedFilter = ref("all");

const isLoading = ref(true);
const errorMessage = ref("");


const filteredEvents = computed(() => {

  switch (selectedFilter.value) {

    case "create":
      return createEvents.value;

    case "edit":
      return editEvents.value;

    default:
      return pendingEvents.value;
  }

});

const showSnackbar =
  ref(false);

const snackbarMessage =
  ref("");

  onMounted(async () => {
  try {
    /*
     * Ждём восстановления Firebase-сессии.
     */
    const user =
      await waitForAuthReady();

    /*
     * Пользователь не авторизован.
     */
    if (!user) {
      console.warn(
        "❌ PendingEvents: пользователь не авторизован"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return;
    }

    /*
     * Проверяем admin flag.
     */
    const adminFlag =
      localStorage.getItem(
        "is_admin"
      ) === "true";

    if (!adminFlag) {
      console.warn(
        "⛔ PendingEvents: нет прав администратора"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return;
    }

    /*
     * Доступ разрешён.
     */
    isAdmin.value =
      true;

    /*
     * Только теперь загружаем ивенты.
     */
    await loadPendingEvents();

  } catch (error) {
    console.error(
      "❌ Ошибка проверки доступа PendingEvents:",
      error
    );

    window.location.replace(
      "https://hr3.space/"
    );

  } finally {
    isAccessChecked.value =
      true;
  }
});
async function approveEvent(event) {

if (
  approvingEventId.value
) {
  return;
}


approvingEventId.value =
  event.id;


try {

  const result =
    await AdminService
      .approvePendingEvent(
        event.id
      );


  console.log(
    "✅ Event approved:",
    result
  );


  pendingEvents.value =
    pendingEvents.value.filter(
      item =>
        item.id !== event.id
    );


  createEvents.value =
    createEvents.value.filter(
      item =>
        item.id !== event.id
    );


  editEvents.value =
    editEvents.value.filter(
      item =>
        item.id !== event.id
    );


  counts.value = {
    total:
      pendingEvents.value.length,

    create:
      createEvents.value.length,

    edit:
      editEvents.value.length
  };


  showSuccessSnackbar(
    event.moderation_type === "edit"
      ? `Изменения «${event.title || "Без названия"}» опубликованы`
      : `Ивент «${event.title || "Без названия"}» опубликован`
  );


} catch (error) {

  console.error(
    "❌ Ошибка публикации ивента:",
    error.response?.data
    || error.message
    || error
  );


  errorMessage.value =
    error.response?.data?.error
    || error.message
    || "Не удалось опубликовать ивент";


} finally {

  approvingEventId.value =
    null;

}

}


async function loadPendingEvents() {

  isLoading.value = true;
  errorMessage.value = "";

  try {

    const result =
      await AdminService.getPendingEvents();

    /*
     * Наш новый AdminService.getPendingEvents()
     * возвращает:
     *
     * {
     *   pendingEvents,
     *   createEvents,
     *   editEvents,
     *   counts
     * }
     */

    pendingEvents.value =
      result?.pendingEvents
      ?? [];

    createEvents.value =
      result?.createEvents
      ?? [];

    editEvents.value =
      result?.editEvents
      ?? [];

    counts.value = {
      total:
        result?.counts?.total
        ?? pendingEvents.value.length,

      create:
        result?.counts?.create
        ?? createEvents.value.length,

      edit:
        result?.counts?.edit
        ?? editEvents.value.length
    };

    console.log(
      "📅 Pending events loaded:",
      {
        total:
          counts.value.total,

        create:
          counts.value.create,

        edit:
          counts.value.edit,

        events:
          pendingEvents.value
      }
    );

  } catch (error) {

    console.error(
      "❌ Ошибка загрузки PendingEvents:",
      error.response?.data
      || error.message
      || error
    );

    errorMessage.value =
      error.response?.data?.error
      || error.message
      || "Произошла неизвестная ошибка";

  } finally {

    isLoading.value = false;

  }
}


/* ============================================================
   MODERATION
============================================================ */

function moderationTitle(event) {

  if (
    event?.moderation_type === "edit"
  ) {
    return "Изменения";
  }

  return "Новый ивент";
}


function moderationBadgeClass(event) {

  return {
    edit:
      event?.moderation_type === "edit",

    create:
      event?.moderation_type !== "edit"
  };
}


function eventCategory(event) {

  if (
    event?.moderation_type === "edit"
  ) {
    return "Редактирование";
  }

  return "Создание";
}


/* ============================================================
   DATES
============================================================ */

function showSuccessSnackbar(
  message
) {

  snackbarMessage.value =
    message;

  showSnackbar.value =
    true;


  window.setTimeout(
    () => {

      showSnackbar.value =
        false;

    },
    3000
  );

}

function timestampToDate(value) {

  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return null;
  }


  if (
    value instanceof Date
  ) {
    return value;
  }


  const numericValue =
    Number(value);


  if (
    !Number.isNaN(numericValue)
  ) {

    /*
     * В Events у нас dateOfEvent хранится
     * строкой timestamp в миллисекундах.
     *
     * Но функция также переживёт timestamp
     * в секундах.
     */

    const milliseconds =
      numericValue < 100000000000
        ? numericValue * 1000
        : numericValue;

    const date =
      new Date(milliseconds);

    if (
      !Number.isNaN(
        date.getTime()
      )
    ) {
      return date;
    }
  }


  const parsedDate =
    new Date(value);

  if (
    Number.isNaN(
      parsedDate.getTime()
    )
  ) {
    return null;
  }

  return parsedDate;
}


function formatEventDate(value) {

  const date =
    timestampToDate(value);

  if (!date) {
    return "Дата не указана";
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      day: "numeric",
      month: "long",
      year: "numeric"
    }
  ).format(date);
}


function eventDay(value) {

  const date =
    timestampToDate(value);

  if (!date) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      day: "2-digit"
    }
  ).format(date);
}


function eventMonth(value) {

  const date =
    timestampToDate(value);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      month: "short"
    }
  )
    .format(date)
    .replace(".", "")
    .toUpperCase();
}


function formatSubmittedDate(event) {

  const value =
    event?.submitted_at
    || event?.date_of_create;

  const date =
    timestampToDate(value);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }
  ).format(date);
}


/* ============================================================
   COMPANY
============================================================ */

function companyInitial(name) {

  if (!name) {
    return "?";
  }

  return name
    .trim()
    .charAt(0)
    .toUpperCase();
}

</script>


<style scoped>

* {
  box-sizing: border-box;
}


/* ============================================================
   PAGE
============================================================ */

.pending-page {
  min-height: 100dvh;

  display: flex;
  flex-direction: column;

  color: #24272d;

  background:
    #f4f6f9;
}


.pending-content {
  flex: 1;

  padding:
    54px
    24px
    80px;

  background:
    radial-gradient(
      circle at 15% 0%,
      rgba(111, 91, 232, 0.09),
      transparent 340px
    ),
    radial-gradient(
      circle at 90% 10%,
      rgba(173, 82, 208, 0.055),
      transparent 320px
    ),
    #f4f6f9;
}


.pending-container {
  width: 100%;
  max-width: 1180px;

  margin: 0 auto;
}


/* ============================================================
   HEADING
============================================================ */

.pending-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 30px;

  margin-bottom: 24px;
}


.pending-label {
  display: block;

  margin-bottom: 6px;

  color: #999fa9;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  letter-spacing: 0.09em;

  text-transform: uppercase;
}


.pending-heading h1 {
  margin: 0;

  color: #22262d;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 30px;

  line-height: 1.2;
}


.pending-heading p {
  max-width: 600px;

  margin:
    9px
    0
    0;

  color: #7c838e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;

  line-height: 1.55;
}


/* ============================================================
   COUNTER
============================================================ */

.pending-counter {
  display: flex;
  align-items: center;

  gap: 7px;

  padding:
    9px
    13px;

  color:
    #7566cf;

  background:
    rgba(
      124,
      92,
      231,
      0.09
    );

  border:
    1px solid
    rgba(
      124,
      92,
      231,
      0.16
    );

  border-radius: 12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;
}


.counter-dot {
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background:
    #7c5ce7;

  box-shadow:
    0 0 0 4px
    rgba(
      124,
      92,
      231,
      0.11
    );
}


.counter-caption {
  opacity: 0.72;
}


/* ============================================================
   FILTERS
============================================================ */

.moderation-filters {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  margin-bottom: 26px;

  padding: 5px;

  background:
    rgba(
      255,
      255,
      255,
      0.75
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.07
    );

  border-radius: 14px;

  box-shadow:
    0 5px 16px
    rgba(
      15,
      23,
      42,
      0.035
    );
}


.filter-button {
  display: flex;
  align-items: center;

  gap: 7px;

  padding:
    9px
    13px;

  color:
    #848b96;

  background:
    transparent;

  border:
    none;

  border-radius: 10px;

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  transition:
    color
    0.18s ease,
    background-color
    0.18s ease,
    box-shadow
    0.18s ease;
}


.filter-button:hover {
  color:
    #6557c9;

  background:
    rgba(
      124,
      92,
      231,
      0.055
    );
}


.filter-button.active {
  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #6f6ee8,
      #8159d7
    );

  box-shadow:
    0 6px 14px
    rgba(
      105,
      83,
      205,
      0.20
    );
}


.filter-count {
  min-width: 20px;

  padding:
    2px
    6px;

  text-align: center;

  background:
    rgba(
      120,
      120,
      130,
      0.11
    );

  border-radius: 999px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 9px;
}


.filter-button.active
.filter-count {
  background:
    rgba(
      255,
      255,
      255,
      0.18
    );
}


/* ============================================================
   GRID
============================================================ */

.events-grid {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(
        0,
        1fr
      )
    );

  gap: 20px;
}


/* ============================================================
   EVENT CARD
============================================================ */

.event-card {
  min-width: 0;

  overflow: hidden;

  background:
    #ffffff;

  border:
    1px solid
    rgba(
      30,
      43,
      61,
      0.07
    );

  border-radius: 22px;

  box-shadow:
    0 10px 30px
    rgba(
      15,
      23,
      42,
      0.05
    );

  transition:
    transform
    0.22s ease,
    border-color
    0.22s ease,
    box-shadow
    0.22s ease;
}


.event-card:hover {
  transform:
    translateY(-3px);

  border-color:
    rgba(
      124,
      92,
      231,
      0.18
    );

  box-shadow:
    0 18px 42px
    rgba(
      45,
      51,
      65,
      0.10
    );
}


/* ============================================================
   IMAGE
============================================================ */

.event-image-wrap {
  position: relative;

  height: 230px;

  overflow: hidden;

  background:
    #eef1f5;
}


.event-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform
    0.35s ease;
}


.event-card:hover
.event-image {
  transform:
    scale(1.015);
}


.event-image-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #aaaebb;

  background:
    linear-gradient(
      135deg,
      #f0f1f7,
      #e6e5f0
    );
}


.event-image-placeholder svg {
  width: 46px;
  height: 46px;
}


/* ============================================================
   MODERATION BADGE
============================================================ */

.moderation-badge {
  position: absolute;

  top: 16px;
  left: 16px;

  padding:
    8px
    11px;

  color:
    #ffffff;

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.16
    );

  border-radius: 10px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.moderation-badge.create {
  background:
    rgba(
      47,
      157,
      103,
      0.88
    );
}


.moderation-badge.edit {
  background:
    rgba(
      109,
      84,
      207,
      0.9
    );
}


/* ============================================================
   DATE BADGE
============================================================ */

.event-date-badge {
  position: absolute;

  top: 16px;
  right: 16px;

  width: 54px;
  height: 54px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  color:
    #22262d;

  background:
    rgba(
      255,
      255,
      255,
      0.92
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.55
    );

  border-radius: 15px;

  box-shadow:
    0 8px 20px
    rgba(
      0,
      0,
      0,
      0.12
    );

  backdrop-filter:
    blur(12px);
}


.event-date-day {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 19px;

  line-height: 1;
}


.event-date-month {
  margin-top: 3px;

  color:
    #777e89;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.05em;
}


/* ============================================================
   BODY
============================================================ */

.event-body {
  padding:
    21px
    21px
    18px;
}


.event-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-bottom: 7px;
}


.event-category {
  min-width: 0;

  overflow: hidden;

  color:
    #8c939e;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  letter-spacing: 0.05em;

  text-overflow: ellipsis;

  text-transform: uppercase;

  white-space: nowrap;
}


.event-created-date {
  flex-shrink: 0;

  color:
    #a3a8b0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


.event-title {
  margin: 0;

  color:
    #25292f;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;

  line-height: 1.3;
}


.event-preview-title {
  margin:
    6px
    0
    0;

  color:
    #7d8490;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  line-height: 1.45;
}


/* ============================================================
   META
============================================================ */

.event-meta {
  display: flex;
  flex-wrap: wrap;

  gap:
    8px
    14px;

  margin-top: 14px;
}


.meta-item {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 5px;

  color:
    #7f8792;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.meta-item svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;
}


.meta-item span {
  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}


/* ============================================================
   COMPANY
============================================================ */

.company-card {
  display: flex;
  align-items: center;

  gap: 11px;

  margin-top: 16px;

  padding:
    11px
    12px;

  background:
    #f7f8fa;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.06
    );

  border-radius: 14px;
}


.company-logo {
  width: 42px;
  height: 42px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  color:
    #6d66a9;

  background:
    linear-gradient(
      135deg,
      #eceaf8,
      #f1f0f8
    );

  border-radius: 12px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;
}


.company-logo img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.company-content {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.company-label {
  color:
    #a0a6af;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.company-name {
  overflow: hidden;

  color:
    #343941;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;

  text-overflow: ellipsis;

  white-space: nowrap;
}


.company-id {
  overflow: hidden;

  color:
    #9ba1ab;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  text-overflow: ellipsis;

  white-space: nowrap;
}


/* ============================================================
   DESCRIPTION
============================================================ */

.event-description {
  display: -webkit-box;

  margin:
    17px
    0
    0;

  overflow: hidden;

  color:
    #676f7a;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  line-height: 1.6;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp:
    5;
}


/* ============================================================
   LINK
============================================================ */

.event-link {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  margin-top: 14px;

  color:
    #5b6fd4;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  text-decoration: none;
}


.event-link:hover {
  text-decoration: underline;
}


.event-link svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   EDIT INFO
============================================================ */

.edit-info {
  display: flex;
  align-items: flex-start;

  gap: 10px;

  margin-top: 16px;

  padding:
    12px
    13px;

  color:
    #6f63b6;

  background:
    rgba(
      124,
      92,
      231,
      0.07
    );

  border:
    1px solid
    rgba(
      124,
      92,
      231,
      0.11
    );

  border-radius: 13px;
}


.edit-info-icon {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #ffffff;

  background:
    #7964d8;

  border-radius: 8px;

  font-size: 12px;
}


.edit-info strong {
  display: block;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.edit-info span {
  display: block;

  margin-top: 3px;

  color:
    #8981b9;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.45;
}


/* ============================================================
   FOOTER
============================================================ */

.event-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  margin-top: 20px;

  padding-top: 17px;

  border-top:
    1px solid
    rgba(
      31,
      44,
      62,
      0.065
    );
}


.event-identifiers {
  min-width: 0;
}


.event-id {
  overflow: hidden;

  color:
    #a1a6ae;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;

  line-height: 1.5;

  text-overflow: ellipsis;

  white-space: nowrap;
}


.event-id span {
  color:
    #858c96;
}


.waiting-button {
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0
    14px;

  color:
    #7a68ca;

  background:
    rgba(
      124,
      92,
      231,
      0.07
    );

  border:
    1px solid
    rgba(
      124,
      92,
      231,
      0.12
    );

  border-radius: 11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.waiting-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background:
    #826ce0;

  box-shadow:
    0 0 0 3px
    rgba(
      130,
      108,
      224,
      0.12
    );
}


/* ============================================================
   FILTER EMPTY
============================================================ */

.filter-empty {
  display: flex;
  align-items: center;

  gap: 13px;

  padding:
    18px
    20px;

  background:
    #ffffff;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.06
    );

  border-radius: 16px;
}


.filter-empty-icon {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #ffffff;

  background:
    #42b878;

  border-radius: 11px;

  font-family:
    "Eastman-Bold",
    sans-serif;
}


.filter-empty strong {
  display: block;

  color:
    #343941;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;
}


.filter-empty span {
  display: block;

  margin-top: 2px;

  color:
    #8c939d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


/* ============================================================
   STATES
============================================================ */

.state-card {
  max-width: 520px;

  margin:
    80px
    auto;

  padding:
    42px
    34px;

  text-align: center;

  background:
    #ffffff;

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.07
    );

  border-radius: 22px;

  box-shadow:
    0 14px 38px
    rgba(
      15,
      23,
      42,
      0.055
    );
}


.state-icon {
  width: 52px;
  height: 52px;

  margin:
    0
    auto
    17px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #ffffff;

  border-radius: 16px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
}


.state-icon.success {
  background:
    linear-gradient(
      135deg,
      #44be7d,
      #2fa86c
    );
}


.state-icon.error {
  background:
    linear-gradient(
      135deg,
      #e76b68,
      #ca4d54
    );
}


.state-card h2 {
  margin: 0;

  color:
    #282c33;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


.state-card p {
  margin:
    9px
    0
    0;

  color:
    #848b95;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  line-height: 1.55;
}


.state-button {
  margin-top: 20px;

  padding:
    11px
    16px;

  cursor: pointer;

  color:
    #ffffff;

  background:
    #6e63da;

  border: 0;

  border-radius: 11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;
}


/* ============================================================
   SKELETON
============================================================ */

.skeleton-card {
  pointer-events: none;
}


.skeleton-image,
.skeleton-line,
.skeleton-pill {
  background:
    linear-gradient(
      90deg,
      #edf0f3 25%,
      #f6f7f9 38%,
      #edf0f3 63%
    );

  background-size:
    400% 100%;

  animation:
    skeleton-shimmer
    1.4s
    ease
    infinite;
}


.skeleton-image {
  height:
    230px;
}


.skeleton-content {
  padding:
    22px;
}


.skeleton-row {
  display: flex;
  justify-content: space-between;

  gap: 20px;
}


.skeleton-line {
  height: 12px;

  margin-bottom: 10px;

  border-radius: 6px;
}


.skeleton-line.small {
  width: 28%;
  height: 8px;
}


.skeleton-line.date {
  width: 20%;
  height: 8px;
}


.skeleton-line.title {
  width: 72%;
  height: 20px;
}


.skeleton-line.short {
  width: 55%;
}


.skeleton-meta {
  display: flex;

  gap: 8px;

  margin-top: 18px;
}


.skeleton-pill {
  width: 90px;
  height: 26px;

  border-radius: 9px;
}


@keyframes skeleton-shimmer {

  0% {
    background-position:
      100% 0;
  }

  100% {
    background-position:
      -100% 0;
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.pending-page {
  color:
    #f2f2f4;

  background:
    #18191c;
}


html[data-theme="dark"]
.pending-content {
  background:
    radial-gradient(
      circle at 15% 0%,
      rgba(111, 91, 232, 0.07),
      transparent 340px
    ),
    radial-gradient(
      circle at 90% 10%,
      rgba(173, 82, 208, 0.05),
      transparent 320px
    ),
    #18191c;
}


html[data-theme="dark"]
.pending-heading h1 {
  color:
    #f2f3f5;
}


html[data-theme="dark"]
.pending-heading p {
  color:
    #939aa5;
}


html[data-theme="dark"]
.pending-label {
  color:
    #747b85;
}


html[data-theme="dark"]
.pending-counter {
  color:
    #b8acf7;

  background:
    rgba(
      124,
      92,
      231,
      0.10
    );

  border-color:
    rgba(
      124,
      92,
      231,
      0.17
    );
}


html[data-theme="dark"]
.moderation-filters {
  background:
    #232428;

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


html[data-theme="dark"]
.event-card,
html[data-theme="dark"]
.state-card,
html[data-theme="dark"]
.filter-empty {
  background:
    #232428;

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );

  box-shadow:
    0 12px 30px
    rgba(
      0,
      0,
      0,
      0.20
    );
}


html[data-theme="dark"]
.event-card:hover {
  border-color:
    rgba(
      133,
      108,
      224,
      0.19
    );

  box-shadow:
    0 18px 44px
    rgba(
      0,
      0,
      0,
      0.32
    );
}


html[data-theme="dark"]
.event-image-wrap {
  background:
    #2d2f34;
}


html[data-theme="dark"]
.event-image-placeholder {
  color:
    #686e78;

  background:
    linear-gradient(
      135deg,
      #292b30,
      #33363c
    );
}


html[data-theme="dark"]
.event-title,
html[data-theme="dark"]
.state-card h2,
html[data-theme="dark"]
.filter-empty strong {
  color:
    #f1f2f4;
}


html[data-theme="dark"]
.event-preview-title,
html[data-theme="dark"]
.event-description {
  color:
    #a0a6af;
}


html[data-theme="dark"]
.event-category,
html[data-theme="dark"]
.event-created-date,
html[data-theme="dark"]
.meta-item {
  color:
    #8e949e;
}


html[data-theme="dark"]
.company-card {
  background:
    #2d2f34;

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.company-logo {
  color:
    #aaa1dd;

  background:
    #36333f;
}


html[data-theme="dark"]
.company-name {
  color:
    #e3e4e7;
}


html[data-theme="dark"]
.company-id {
  color:
    #80858e;
}


html[data-theme="dark"]
.edit-info {
  color:
    #baadf2;

  background:
    rgba(
      124,
      92,
      231,
      0.09
    );

  border-color:
    rgba(
      124,
      92,
      231,
      0.14
    );
}


html[data-theme="dark"]
.edit-info span {
  color:
    #9e95c6;
}


html[data-theme="dark"]
.event-footer {
  border-top-color:
    rgba(
      255,
      255,
      255,
      0.07
    );
}


html[data-theme="dark"]
.skeleton-image,
html[data-theme="dark"]
.skeleton-line,
html[data-theme="dark"]
.skeleton-pill {
  background:
    linear-gradient(
      90deg,
      #292b30 25%,
      #34363c 38%,
      #292b30 63%
    );

  background-size:
    400% 100%;
}


/* ============================================================
   TABLET
============================================================ */

@media (
  max-width: 900px
) {

  .pending-content {
    padding:
      40px
      18px
      70px;
  }


  .events-grid {
    grid-template-columns:
      1fr;
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 600px
) {

  .pending-content {
    padding:
      30px
      14px
      60px;
  }


  .pending-heading {
    flex-direction:
      column;

    gap: 16px;

    margin-bottom:
      20px;
  }


  .pending-heading h1 {
    font-size:
      25px;
  }


  .pending-heading p {
    font-size:
      12px;
  }


  .moderation-filters {
    width: 100%;

    overflow-x: auto;

    margin-bottom:
      20px;
  }


  .filter-button {
    flex:
      1;

    justify-content:
      center;

    white-space:
      nowrap;
  }


  .event-card {
    border-radius:
      18px;
  }


  .event-image-wrap,
  .skeleton-image {
    height:
      190px;
  }


  .event-body {
    padding:
      18px
      16px
      16px;
  }


  .event-title {
    font-size:
      17px;
  }


  .event-footer {
    flex-direction:
      column;

    align-items:
      stretch;
  }


  .waiting-button {
    width:
      100%;
  }


  .event-id {
    text-align:
      center;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .event-card,
  .filter-button {
    transition:
      none;
  }


  .skeleton-image,
  .skeleton-line,
  .skeleton-pill {
    animation:
      none;
  }

}

.approve-button {
  min-width: 145px;
  height: 40px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0
    15px;

  cursor: pointer;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #42b878,
      #2ea46a
    );

  border: 0;

  border-radius: 11px;

  box-shadow:
    0 8px 18px
    rgba(
      46,
      164,
      106,
      0.18
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease,
    opacity
    0.18s ease;
}


.approve-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 11px 22px
    rgba(
      46,
      164,
      106,
      0.25
    );
}


.approve-button:disabled {
  cursor: default;

  opacity: 0.72;
}


.approve-button svg {
  width: 15px;
  height: 15px;
}


.button-spinner {
  width: 13px;
  height: 13px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    button-spin
    0.75s
    linear
    infinite;
}


@keyframes button-spin {
  to {
    transform:
      rotate(360deg);
  }
}

</style>